import { createSlice, createAsyncThunk } from "@reduxjs/toolkit";
import {
  getResourceAllocations,
  addResourceAllocation,
  updateResourceAllocation,
  deleteResourceAllocation,
  getResourceAllocationById,
} from "./resourceAllocationApi";

const handleApiError = (err) =>
  (err?.response?.data?.msg || err?.message || "Unknown error");

// Thunks
export const fetchResourceAllocations = createAsyncThunk(
  "resourceAllocations/fetchAssistantSuperadmin",
  async (id, { rejectWithValue }) => {
    try {
      const response = await getResourceAllocations(id);
      return response.data;
    } catch (err) {
      return rejectWithValue(handleApiError(err));
    }
  }
);
export const fetchResourceAllocationById = createAsyncThunk(
  "resourceAllocations/fetchResourceAllocationById",
  async (_id, { rejectWithValue }) => {
    try {
      const response = await getResourceAllocationById(_id);
      return response.data;
    } catch (err) {
      return rejectWithValue(handleApiError(err));
    }
  }
);

export const createResourceAllocation = createAsyncThunk(
  "resourceAllocations/createResourceAllocation",
  async (data, { rejectWithValue }) => {
    try {
      const response = await addResourceAllocation(data);
      return response;
    } catch (err) {
      return rejectWithValue(handleApiError(err));
    }
  }
);

export const updateResourceAllocationData = createAsyncThunk(
  "resourceAllocations/updateResourceAllocation",
  async (data, { rejectWithValue }) => {
    try {
      const response = await updateResourceAllocation(data);
      return response;
    } catch (err) {
      return rejectWithValue(handleApiError(err));
    }
  }
);

export const deleteResourceAllocationById = createAsyncThunk(
  "resourceAllocations/deleteResourceAllocation",
  async (id, { rejectWithValue }) => {
    try {
      const response = await deleteResourceAllocation(id);
      return response;
    } catch (err) {
      return rejectWithValue(handleApiError(err));
    }
  }
);

const resourceAllocationSlice = createSlice({
  name: "resourceAllocations",
  initialState: {
    resourceAllocations: [],
    loading: false,
    error: null,
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchResourceAllocations.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchResourceAllocations.fulfilled, (state, action) => {
        state.loading = false;
        state.resourceAllocations = action.payload;
      })
      .addCase(fetchResourceAllocations.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch resource allocations";
      })
      .addCase(fetchResourceAllocationById.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchResourceAllocationById.fulfilled, (state, action) => {
        state.loading = false;
        state.resourceAllocations = action.payload;
      })
      .addCase(fetchResourceAllocationById.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload || "Failed to fetch resource allocation";
      })

      .addCase(createResourceAllocation.fulfilled, (state, action) => {
        state.error = null;
      })
      .addCase(updateResourceAllocationData.fulfilled, (state, action) => {
        state.error = null;
      })
      .addCase(deleteResourceAllocationById.fulfilled, (state, action) => {
        state.error = null;
      })
      .addCase(createResourceAllocation.pending, (state) => {
        state.error = null;
      })
      .addCase(createResourceAllocation.rejected, (state, action) => {
        state.error = action.payload || "Failed to create resource allocation";
      })
      .addCase(updateResourceAllocationData.pending, (state) => {
        state.error = null;
      })
      .addCase(updateResourceAllocationData.rejected, (state, action) => {
        state.error = action.payload || "Failed to update resource allocation";
      })
      .addCase(deleteResourceAllocationById.pending, (state) => {
        state.error = null;
      })
      .addCase(deleteResourceAllocationById.rejected, (state, action) => {
        state.error = action.payload || "Failed to delete resource allocation";
      });
  },
});

export default resourceAllocationSlice.reducer;
